import { vmColor } from "@helpers/themeColor"
import { useTranslation } from "react-i18next"

const WIDTH = 320
const HEIGHT = 170
const BASELINE_Y = 150
const START = { x: 24, y: 128 }
const END = { x: 292, y: 42 }
const CURVE = `M${START.x} ${START.y} C 55 128, 70 96, 100 98 S 140 112, 165 84 S 215 62, 240 64 S 280 54, ${END.x} ${END.y}`

interface Props {
  goalLabel: string
  startLabel: string
  targetLabel: string
}

function toPercent(x: number, y: number) {
  return { left: `${(x / WIDTH) * 100}%`, top: `${(y / HEIGHT) * 100}%` }
}

export default function ProgressForecastChart({
  goalLabel,
  startLabel,
  targetLabel,
}: Props) {
  const { t } = useTranslation()

  return (
    <div className="vm-card w-full px-4 pb-4 pt-14">
      <div className="relative aspect-[320/170]">
        <svg
          className="absolute inset-0 h-full w-full overflow-visible"
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        >
          <defs>
            <linearGradient id="forecast-fill" x1="0" x2="0" y1="0" y2="1">
              <stop
                offset="0%"
                style={{ stopColor: vmColor("primary"), stopOpacity: 0.22 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: vmColor("primary"), stopOpacity: 0 }}
              />
            </linearGradient>
            <linearGradient id="forecast-stroke" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0%" style={{ stopColor: vmColor("primary") }} />
              <stop
                offset="60%"
                style={{ stopColor: vmColor("primary-text") }}
              />
              <stop offset="100%" style={{ stopColor: vmColor("green") }} />
            </linearGradient>
          </defs>

          {[30, 60, 90, 120].map((y) => (
            <line
              key={y}
              style={{ stroke: vmColor("line") }}
              x1="8"
              x2={WIDTH - 8}
              y1={y}
              y2={y}
            />
          ))}
          <line
            style={{ stroke: vmColor("line") }}
            x1="8"
            x2={WIDTH - 8}
            y1={BASELINE_Y}
            y2={BASELINE_Y}
          />

          <path
            className="animate-fade-in [animation-delay:900ms]"
            d={`${CURVE} L ${END.x} ${BASELINE_Y} L ${START.x} ${BASELINE_Y} Z`}
            fill="url(#forecast-fill)"
          />

          <line
            className="animate-fade-in"
            style={{ stroke: vmColor("primary") }}
            strokeWidth="2"
            x1={START.x}
            x2={START.x}
            y1={START.y}
            y2={BASELINE_Y}
          />
          <line
            className="animate-fade-in [animation-delay:1300ms]"
            style={{ stroke: vmColor("green") }}
            strokeWidth="2"
            x1={END.x}
            x2={END.x}
            y1={END.y}
            y2={BASELINE_Y}
          />

          <path
            className="animate-draw"
            d={CURVE}
            fill="none"
            pathLength={1}
            stroke="url(#forecast-stroke)"
            strokeDasharray="1"
            strokeLinecap="round"
            strokeWidth="6"
          />

          <circle
            cx={START.x}
            cy={START.y}
            r="6"
            strokeWidth="2.5"
            style={{ fill: vmColor("primary"), stroke: vmColor("surface") }}
          />
          <g
            className="animate-scale-in [animation-delay:1300ms]"
            style={{ transformBox: "fill-box", transformOrigin: "center" }}
          >
            <circle
              cx={END.x}
              cy={END.y}
              r="7"
              strokeWidth="2.5"
              style={{ fill: vmColor("green"), stroke: vmColor("surface") }}
            />
          </g>
        </svg>

        <div
          className="absolute -translate-x-1/2 -translate-y-full pb-3"
          style={toPercent(START.x + 8, START.y)}
        >
          <div className="relative animate-fade-up rounded-lg bg-vm-primary-solid px-2.5 py-1 text-xs font-bold text-white [animation-delay:300ms]">
            {t("pages.checkout.paywall.forecast.now")}
            <span className="absolute -bottom-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-vm-primary-solid" />
          </div>
        </div>

        <div
          className="absolute -translate-x-[85%] -translate-y-full pb-3"
          style={toPercent(END.x, END.y)}
        >
          <div className="relative animate-fade-up whitespace-nowrap rounded-lg bg-vm-green px-3 py-1.5 text-center text-xs font-bold leading-tight text-vm-on-green [animation-delay:1500ms]">
            {goalLabel}
            <span className="absolute -bottom-1 right-[15%] h-2 w-2 rotate-45 bg-vm-green" />
          </div>
        </div>
      </div>

      <div className="mt-2 flex justify-between text-[11px] font-bold uppercase tracking-wide">
        <span className="text-vm-primary-text">{startLabel}</span>
        <span className="text-vm-green-text">{targetLabel}</span>
      </div>
    </div>
  )
}
